'use client'

import { forwardRef, type ComponentProps } from 'react'
import { createNavigation } from 'next-intl/navigation'
import NextLink from 'next/link'

import { useDisplayCurrency } from '@/lib/currency/display-currency'
import { hrefWithDisplayCurrency } from '@/lib/currency/link'
import { useLocale } from '@/i18n/locale'

import { isDefaultLocaleHomepage } from './homepage-href'
import { localeOfPrefixedHref, needsNoLocalePrefix } from './locale-prefix'
import { routing } from './routing'

const nav = createNavigation(routing)

type IntlLinkProps = ComponentProps<typeof nav.Link>

/**
 * `native` renders a plain anchor: a full document navigation, with nothing attached that
 * prefetches or client-navigates. For a destination **another application serves** — the
 * legacy tree, an off-site URL — where a routed link would speculatively fetch a page this
 * app cannot render, and on click attempt a client-side navigation it cannot complete.
 *
 * A native href is a **string**, stated by the type rather than by a note a caller has to
 * read: the anchor emits one attribute, and an object form would render as
 * `[object Object]`. Its locale prefix and its display currency are applied here, the same
 * way a routed href's are, so the two kinds of link cannot resolve one address differently.
 */
type LinkProps =
    | (IntlLinkProps & { native?: false })
    | (Omit<ComponentProps<'a'>, 'href'> & {
          native: true
          href: string
          locale?: IntlLinkProps['locale']
          /** Accepted so a caller can set it whatever `native` is; a plain anchor ignores it. */
          prefetch?: IntlLinkProps['prefetch']
      })

// Isolated in a `'use client'` module, like `useRouter`: reading the selected display
// currency is a context read, which only a client component can do, and importing a
// client hook into the server-safe `@/i18n/navigation` module would taint every server
// consumer of `redirect` and `getPathname`. `@/i18n/navigation` re-exports this, so the
// import surface every call site uses is unchanged.
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(props, ref) {
    const activeLocale = useLocale()
    const selectedCurrency = useDisplayCurrency()

    const { href, locale } = props
    const targetLocale = locale ?? activeLocale

    // Both decisions read the href as authored, before the currency is appended: `/` is
    // the default-locale homepage to `isDefaultLocaleHomepage`, `/?cur=EUR` is not.
    const isAlreadyFinal = isDefaultLocaleHomepage(href, targetLocale) || needsNoLocalePrefix(href)

    // The destination's locale, whose default currency decides whether the selected one
    // travels: the one an already-prefixed href names, else the one this link was told,
    // else the active locale. A locale-free surface has none, and so carries nothing —
    // the href is tested first, so a `locale` prop supplied alongside such an href cannot
    // give it a locale it does not have.
    const destinationLocale = needsNoLocalePrefix(href) ? localeOfPrefixedHref(href) : targetLocale

    if (props.native) {
        const {
            native: _native,
            href: authored,
            locale: _locale,
            prefetch: _prefetch,
            ...anchorProps
        } = props
        const address = hrefWithDisplayCurrency(authored, selectedCurrency, destinationLocale)
        // Prefixed here, not at the call site: the anchor emits its href verbatim, so
        // nothing downstream puts the locale on, and a locale-less path reaches the legacy
        // origin and redirects. Only a single-leading-slash path is one of ours to prefix —
        // a foreign origin and a protocol-relative URL are addresses, not routes.
        const isOwnPath = address.startsWith('/') && !address.startsWith('//')
        return (
            <a
                ref={ref}
                href={isAlreadyFinal || !isOwnPath ? address : `/${targetLocale}${address}`}
                {...anchorProps}
            />
        )
    }

    const { native: _native, href: routedHref, locale: _locale, ...rest } = props
    const finalHref = hrefWithDisplayCurrency(routedHref, selectedCurrency, destinationLocale)

    // Two families bypass next-intl's prefixing, both because prefixing would
    // produce a URL that redirects: the default-locale homepage (rendered as the
    // bare `/`) and an href that is already final — locale-prefixed, or on
    // a locale-free surface — which renders verbatim. Everything else keeps
    // next-intl's prefixing.
    if (isAlreadyFinal) {
        // next-intl and next/link resolve slightly different React anchor-attribute
        // types (e.g. the `popover` union), so narrow `rest` to next/link's props.
        const nextLinkProps = rest as Omit<ComponentProps<typeof NextLink>, 'href'>
        return (
            <NextLink
                ref={ref}
                href={finalHref as ComponentProps<typeof NextLink>['href']}
                {...nextLinkProps}
            />
        )
    }

    return <nav.Link ref={ref} href={finalHref} locale={locale} {...rest} />
})
